import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CHIP, CategoryChip, Countdown, VenueChip } from '../describe/ui';
import TagLabel from './TagLabel';
import type { Category } from '../describe';
import type { DayVolume } from '../volume/volume';
import { formatCents, type CatalogItem, type SortKey } from './catalog';
import { MAX_QUESTION_ROWS, questionRows } from './group';
import { panelClick } from './layout';
import { useT } from '../i18n/useT';
import { VolumeCompact } from '../volume/VolumeCells';
import { formatVolumeUsd } from '../volume/format';

export interface QuestionCardData {
  question: number;
  title: string;
  subtitle?: string;
  category: Category;
  resolvesAt?: Date;
  resolvesLabel?: string;
  /** Named outcomes still trading (already filtered), each with its own label. */
  rows: { item: CatalogItem; label: string }[];
  /** The fallback ("Other") outcome, if listed and matching the filter. */
  fallback?: { item: CatalogItem; label: string };
  /** How many named outcomes have settled (hidden). */
  settled: number;
  /** 24 h volume summed over the question's named outcomes (#874). */
  volume?: DayVolume;
  /** Market tag shown as the card label (#875), if any. */
  tagLabel?: string;
}

export interface QuestionCardProps {
  group: QuestionCardData;
  linkSearch: string;
  /** Catalog sort: rows follow the 24 h volume when it is `vol` (#874), else the Yes price. */
  sort?: SortKey;
  /** Outcome open in the inline panel (#1953), highlighted; null for none. */
  selected?: number | null;
  /** Opens an outcome in the inline panel; without it a row is a plain link to the market page. */
  onSelect?: (outcome: number) => void;
}

interface RowProps {
  item: CatalogItem;
  label: string;
  linkSearch: string;
  selected: boolean;
  onSelect?: (outcome: number) => void;
}

function Row({ item, label, linkSearch, selected, onSelect }: RowProps) {
  const t = useT();
  return (
    <li>
      <Link
        to={`/market/${item.outcome}${linkSearch}`}
        onClick={panelClick(item.outcome, onSelect)}
        className={`flex items-center justify-between gap-3 rounded-md border px-2 py-1.5 text-sm transition-colors ${
          selected ? 'border-accent bg-accent/15' : 'border-transparent hover:bg-surface-2'
        }`}
        data-testid="question-outcome"
        data-outcome-link={item.outcome}
        data-selected={selected ? 'true' : undefined}
        aria-current={selected ? 'true' : undefined}
        aria-label={t('catalog.tile_aria', { id: String(item.outcome), title: item.title })}
      >
        <span className="min-w-0 flex-1 truncate" translate="no">{label}</span>
        <VolumeCompact volume={item.volume} testId="question-volume-compact" />
        <span className="num hidden w-16 shrink-0 text-end text-xs text-muted sm:inline" data-testid="question-volume">
          {formatVolumeUsd(item.volume?.usd)}
        </span>
        <span className={`num w-12 shrink-0 text-end font-semibold ${item.mid !== undefined ? 'text-yes' : 'text-muted'}`}>
          {formatCents(item.mid)}
        </span>
      </Link>
    </li>
  );
}

function QuestionCardImpl({ group, linkSearch, sort, selected = null, onSelect }: QuestionCardProps) {
  const t = useT();
  const venue = group.rows[0]?.item.venue ?? group.fallback?.item.venue;
  const rows = questionRows(group, sort);
  const [open, setOpen] = useState(false);
  // A selected outcome is never folded away.
  const all = open || rows.findIndex((r) => r.item.outcome === selected) >= MAX_QUESTION_ROWS;
  const shown = all ? rows : rows.slice(0, MAX_QUESTION_ROWS);
  const more = rows.length - shown.length;
  return (
    <li className="card flex h-full flex-col gap-2 p-3 sm:p-4" data-testid="question-card" aria-label={t('catalog.question_aria', { title: group.title })}>
      <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted">
        <CategoryChip category={group.category} />
        {group.tagLabel ? <TagLabel tag={group.tagLabel} /> : null}
        <span className={`num ${CHIP}`}>Q{group.question}</span>
        {venue ? <VenueChip venue={venue} /> : null}
        <span className={CHIP}>
          {t('catalog.question_outcomes', { n: group.rows.length + (group.fallback ? 1 : 0) })}
          {group.settled ? ` · ${t('catalog.question_settled', { n: group.settled })}` : ''}
        </span>
        {group.volume ? (
          <span className="num ms-auto" data-testid="question-total-volume">
            {t('volume.compact', { amount: formatVolumeUsd(group.volume.usd) })}
          </span>
        ) : null}
      </div>
      <div>
        <div className="break-words font-medium leading-snug" data-testid="question-title" translate="no">
          {group.title}
        </div>
        {group.subtitle ? <div className="mt-0.5 line-clamp-2 break-words text-xs text-muted" translate="no">{group.subtitle}</div> : null}
      </div>
      <div className="flex justify-between gap-3 text-[11px] uppercase tracking-wide text-muted">
        <span className="flex-1">{t('catalog.col_outcome')}</span>
        <span className="hidden w-16 text-end sm:inline">{t('volume.col_24h')}</span>
        <span className="w-12 text-end">{t('common.yes')}</span>
      </div>
      <ul className="-mx-2 flex flex-col">
        {shown.map((r) => (
          <Row
            key={r.item.outcome}
            item={r.item}
            label={r.label}
            linkSearch={linkSearch}
            selected={r.item.outcome === selected}
            onSelect={onSelect}
          />
        ))}
        {group.fallback ? (
          <Row
            item={group.fallback.item}
            label={group.fallback.label}
            linkSearch={linkSearch}
            selected={group.fallback.item.outcome === selected}
            onSelect={onSelect}
          />
        ) : null}
      </ul>
      <div className="mt-auto flex items-center justify-between gap-2 text-xs text-muted">
        {more > 0 ? (
          <button type="button" className="text-accent hover:underline" onClick={() => setOpen(true)}>
            {t('catalog.show_all', { n: rows.length })}
          </button>
        ) : (
          <span />
        )}
        {group.resolvesAt ? <Countdown date={group.resolvesAt} label={group.resolvesLabel} /> : null}
      </div>
    </li>
  );
}

const QuestionCard = memo(QuestionCardImpl);
export default QuestionCard;
